import featureFlagStore from "@/store/featureFlagStore";
import { fetchRequestWrapper } from "@/lib/utilities";
import { logClientError } from "@/lib/errorLoggingACTIONS";

// The /Settings/Features endpoint returns a list of name/value pairs where the
// value is a *stringified* boolean, e.g.
//   { settings: [{ name: "TradeSettingsSidebarAndCogFeature", value: "true" }] }
export interface FeatureSetting {
  name: string;
  value: string;
}

// Everything below is backend-driven. rpRequestInformationButtons is hardcoded on in
// fetchFeatureFlags until the backend provides it; tradeCreationEditBlocks is front-end
// only and keeps its featureFlagStore default (never in this map).
type FeatureFlagKey =
  | "tradeSettingsSidebarAndCog"
  | "icebergForOrders"
  | "expiryTypeDay"
  | "expiryTypeTime"
  | "tradeExpiryColumn"
  | "tradeExpiryEdit"
  | "tradeExpiryCountdown"
  | "offloadRisk"
  | "tradeCreationLegs";

// Backend feature name -> local store flag. Keyed lowercase so the lookup is
// case-insensitive; this guards against casing drift from the backend (e.g.
// "Rp" vs "RP") silently leaving a feature off.
const FEATURE_NAME_TO_FLAG: Record<string, FeatureFlagKey> = {
  tradesettingssidebarandcogfeature: "tradeSettingsSidebarAndCog",
  icebergforordersfeature: "icebergForOrders",
  expirytypedayfeature: "expiryTypeDay",
  expirytypetimefeature: "expiryTypeTime",
  tradeexpirycolumnfeature: "tradeExpiryColumn",
  tradeexpiryeditfeature: "tradeExpiryEdit",
  tradeexpirycountdownfeature: "tradeExpiryCountdown",
  offloadriskfeature: "offloadRisk",
  tradecreationlegsfeature: "tradeCreationLegs",
};

// Pure: turn the API's name/value list into a partial flags object.
// - Unknown feature names are ignored.
// - Flags absent from the response keep their store default (off).
// - value is compared as a lowercased string ("true" => true, anything else => false).
export function mapFeatureSettingsToFlags(
  settings: FeatureSetting[] | undefined,
): Partial<Record<FeatureFlagKey, boolean>> {
  const flags: Partial<Record<FeatureFlagKey, boolean>> = {};
  for (const setting of settings ?? []) {
    const key = FEATURE_NAME_TO_FLAG[setting?.name?.toLowerCase?.()];
    if (key) flags[key] = String(setting?.value).toLowerCase() === "true";
  }
  return flags;
}

export async function fetchFeatureFlags(getAccessTokenSilently: Function) {
  try {
    const response = await fetchRequestWrapper(
      { url: "/Settings/Features", method: "GET" },
      getAccessTokenSilently,
    );

    featureFlagStore.getState().setFeatureFlags({
      ...mapFeatureSettingsToFlags(response?.settings),
      // Not yet provided by the backend — hardcoded on (as it was before).
      rpRequestInformationButtons: true,
      featureFlagsLoaded: true,
    });
  } catch (error) {
    // Backend-driven flags default to off on failure; the hardcoded ones stay on.
    // Still mark as loaded so the app renders.
    featureFlagStore.getState().setFeatureFlags({
      rpRequestInformationButtons: true,
      featureFlagsLoaded: true,
    });
    // Log
    logClientError(
      "featureFlag.fetchingFeatureFlags",
      error,
      getAccessTokenSilently,
    );
  }
}
